import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useFormikContext } from 'formik';
import { resetForm } from '../../../../../../../../../../store/leaseForm/actions';

// ─── MUI Components ────────────────────────────────────────────────
import { IconButton, Tooltip } from '@mui/material';
import FeatherIcon from 'feather-icons-react';

// ─── Helpers ───────────────────────────────────────────────────────
import Swal from 'sweetalert2';
import { deleteLeaseById } from '../../utils/api';

/**
 * FormResetButton Component
 * Button to reset the form fields to initial values, delete lease if leaseId exists, and clear session storage
 *
 * @param {Object} props - Component props
 * @param {string} props.property_id - Property ID
 * @param {string} props.building_name - Building name
 * @param {string} props.unit_no - Unit number
 * @param {string} props.leaseId - Lease ID
 */
const FormResetButton = React.memo(({ leaseId }) => {
  const { property_id, unit_no, building_name } = useParams();
  const formik = useFormikContext();
  const dispatch = useDispatch();

  const handleResetForm = useCallback(async () => {
    if (!formik) {
      console.error('Formik context not available');
      return;
    }

    const result = await Swal.fire({
      title: leaseId ? 'Cancel Lease' : 'Reset Form',
      text: leaseId
        ? 'Are you sure you want to cancel this lease? This will permanently delete all lease information from the server and cannot be undone.'
        : 'Are you sure you want to reset all form fields? This will clear all unsaved changes.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: leaseId ? 'Yes, Cancel Lease' : 'Yes, Reset Form',
      cancelButtonText: 'Keep Changes'
    });

    if (!result.isConfirmed) {
      return;
    }

    // Show loading indicator
    Swal.fire({
      title: leaseId ? 'Canceling lease...' : 'Resetting form...',
      text: 'Please wait while we process your request.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      if (leaseId) {
        await deleteLeaseById(leaseId);
      }

      sessionStorage.clear();
      dispatch(resetForm());
      formik.resetForm();

      Swal.fire(
        leaseId ? 'Lease Canceled!' : 'Form Reset!',
        leaseId
          ? 'The lease has been successfully canceled and all information has been deleted from the server.'
          : 'The form has been reset and all changes have been cleared.',
        'success'
      );

      window.location.reload();
    } catch (error) {
      console.error('Error during form reset:', error);
      Swal.fire(
        'Error',
        leaseId
          ? 'There was a problem canceling the lease. Please try again or contact support if the issue persists.'
          : 'There was a problem resetting the form. Please try again.',
        'error'
      );
    }
  }, [formik, dispatch, property_id, building_name, unit_no, leaseId]);

  return (
    <Tooltip title={leaseId ? "Cancel Lease" : "Reset Form"}>
      <IconButton onClick={handleResetForm}>
        <FeatherIcon icon="rotate-ccw" size={15} />
      </IconButton>
    </Tooltip>
  );
});

export default FormResetButton;